import { useState } from "react";
import {
  Drawer,
  Button,
  Input,
  Form,
  message,
  Typography,
  Image,
  Spin,
  Upload,
  Select,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_USER, GET_LOCATIONS } from "./requests";
import { userTypes } from "../../../../types";
import useUpload from "../../../../hooks/use-upload";
import { staffUserTypes } from "../../../../utils/constants";

const { Text } = Typography;
const { Option } = Select;

interface UserDrawerProps {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  refetch: () => void;
  onUserCreated: (user: typeof userTypes.user) => void;
  isLocationView: boolean;
}

const CreateUser = ({
  showModal,
  setShowModal,
  refetch,
  onUserCreated,
  isLocationView,
}: UserDrawerProps) => {
  const [form] = Form.useForm<typeof userTypes.user>();
  const { upload } = useUpload();
  const [loading, setLoading] = useState(false);
  const [uploadLoding, setUploadLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [pageConfig, setPageConfig] = useState({ page: 1,
    pageSize: 15 });
  const [searchName, setSearchName] = useState("");

  const { data, loading: loadingLocations } = useQuery(GET_LOCATIONS,
    {
      variables: {
        params: pageConfig,
        name: searchName,
      },
    });

  const locations = data?.locations.results ?? [];

  const [createUser] = useMutation(CREATE_USER);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      const payload = { variables: { user: { ...values,
        img: imageUrl ?? "" } } };
      const { data } = await createUser(payload);

      if (isLocationView) {
        const newUser = {
          id: data.createUser.user.id,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          password: values.password,
          type: values.type,
        };

        onUserCreated(newUser);
      }

      await refetch();
      message.success("Usuario creado con exito.")
      onClose();
    } catch (error) {
      console.log(error);
      message.error("Error al crear usuario");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async ({ fileList }) => {
    if (fileList[0]) {
      const fileName = `users/${new Date().toISOString()}/${
        fileList[0]?.name
      }`;
      setUploadLoading(true);
      const url = await upload(fileList[0].originFileObj, fileName);

      setImageUrl(url);
    }
    setUploadLoading(false);
    message.success("Imagen agregada.");
  };

  const onClose = () => {
    setShowModal(false);
    form.resetFields();
    setImageUrl(null);
    setSearchName("");
  }

  return (
    <Drawer
      title="Crear Nuevo Usuario"
      open={showModal}
      onClose={onClose}
      width={"40%"}>
      <Form form={form} layout="vertical">
        <Form.Item
          name="firstName"
          label="Nombre"
          rules={[{ required: true }]}
        >
          <Input placeholder="Ingrese el nombre" />
        </Form.Item>
        <Form.Item
          name="lastName"
          label="Apellido"
          rules={[{ required: true }]}
        >
          <Input placeholder="Ingrese el apellido" />
        </Form.Item>
        <Form.Item
          name="email"
          label="Correo"
          rules={[{ required: true }]}
        >
          <Input placeholder="Ingrese el correo" />
        </Form.Item>
        <Form.Item
          name="type"
          label="Tipo"
          rules={[{ required: true }]}
        >
          <Select
            placeholder="Selecciona el tipo"
            allowClear
          >
            {Object.keys(staffUserTypes).map((key) => (
              <Option key={key} value={key}>
                {staffUserTypes[key as keyof typeof staffUserTypes].name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="password"
          label="Contraseña"
          rules={[{ required: true }]}
        >
          <Input.Password placeholder="Ingrese la contraseña" />
        </Form.Item>
        <Form.Item
          name="location"
          label="Franquicia"
          rules={[{ required: true,
            message: "Selecciona una Franquicia" }]}
        >
          <Select
            showSearch
            placeholder="Busca o selecciona una Franquicia"
            filterOption={false}
            onSearch={(value) => {
              setSearchName(value);
              setPageConfig({ ...pageConfig,
                page: 1 });
            }}
            loading={loadingLocations}
            allowClear
          >
            {locations.map((loc) => (
              <Option key={loc.id} value={loc.id}>
                {loc.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
      <div>
        <Text>Subir Imagen</Text>
        <Upload
          beforeUpload={() => false}
          listType="picture"
          maxCount={1}
          accept="image/*"
          onChange={(info) => {
            handleFileUpload(info);
          }}
          showUploadList={false}
        >
          <Button style={{ marginLeft: 10 }} icon={<UploadOutlined />}>Seleccionar Imagen</Button>
          {uploadLoding ? <Spin style={{ margin: 10 }} /> : null}
        </Upload>
        {imageUrl && (
          <Image style={{ marginLeft: 20 }} width={140} src={imageUrl} />
        )}
      </div>
      <Button
        size="large"
        loading={loading}
        type="primary"
        onClick={handleSubmit}
        style={{ marginTop: 40 }}>
        Crear Usuario
      </Button>
    </Drawer>
  );
};

export default CreateUser;
